import React, { useContext, useState } from 'react'
import { Button, Col, Container, Inline, Modal, Row } from '@airbus/components-react/components'
import { useTranslation } from 'react-i18next'
import PageHeader from 'components/pageHeader'
import { useForm, FormProvider, SubmitErrorHandler } from 'react-hook-form'
import useMutation from 'utils/hooks/useMutation'
import { ManHourInput, UserQualification } from 'types/workorder/manHour'
import URL_APIS from 'utils/api/urlApis'
import { Lang } from 'context/LangContext'
import { format, isValid, parse } from 'date-fns'
import { DisplayModus } from 'constants/constants'
import ComponentsUNames from 'constants/componentsUNames'
import LocalStorage from 'utils/myLocalStorage'
import ModalConfirm from 'components/Modal/ModalConfim'
import FormDate from 'components/FormFields/FormDate'
import FormTime from 'components/FormFields/FormTime'
import { manageFormErrors } from 'components/FormFields/FormField'
import { Message, MessageSeverity } from 'constants/message'
import ModalStaff from 'components/Modal/modalStaff'
import ModalQualification from 'components/Modal/modalQualification'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons'
import FormInput from 'components/FormFields/FormInput'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MessageContext from 'context/MessageContext'
import LightDarkContext from 'context/LightDarkModeContext'
import useQuery from 'utils/hooks/useQuery'

// List mapping fields in error after a submission returning errors/warnings
const messagesErrWarnFromBack = {
  OLD8034: {
    //message: "Cette zone doit être renseignée",
    severity: MessageSeverity.ERROR,
    fieldNames: ['mohdep', 'hfin'],
  },
  OLD7330: {
    // Qualification inconnue pour cette personne
    severity: MessageSeverity.ERROR,
    fieldNames: ['qualification'],
  },
}

type Props = {
  lang: Lang
  open: boolean
  displayMode: DisplayModus.CREATE | DisplayModus.WRITE
  onClose: () => void
  manHour?: ManHourInput
  onPostSubmit: () => void
}

const styleModal = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  border: '2px solid #000',
  boxShadow: 24,
  height: '60%',
  width: '60%',
  overflow: 'hidden',
}

const ModalManHour = ({ lang, open, displayMode, onClose, manHour, onPostSubmit }: Props) => {
  const { t } = useTranslation()
  const formTitle = t('8000113')
  const { handleDisplayMessage } = useContext(MessageContext)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [modalStaffOpened, setModalStaffOpened] = useState(false)
  const [modalQualificationOpened, setModalQualificationOpened] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { fetchData: fetchStaff } = useQuery<UserQualification[]>(URL_APIS.URL_WORKORDER_PERSONNEL_SEARCH_GET)
  const { mutate: createOrUpdateManHour } = useMutation<ManHourInput>(URL_APIS.URL_WORKORDER_MANHOUR_POST_PUT)

  const userCode = LocalStorage.getOrDefault([LocalStorage.KEY_USER], LocalStorage.VALUE_TYPE.STRING, undefined)
  const canCreate = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_CREATE_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )
  const canUpdate = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_UPDATE_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const methods = useForm<ManHourInput>({
    values: manHour,
  })

  const {
    handleSubmit,
    setValue,
    watch,
    setError,
    formState: { errors, isDirty },
    reset,
  } = methods

  const selectedUser = watch('user')

  const onSubmit = async (data: ManHourInput) => {
    setIsLoading(true)

    try {
      // First fetch appl associated to { staff; qualification }
      const staffResponse = await fetchStaff({
        lang: lang,
        code: data.user.toUpperCase(),
        qualif: data.qualification?.toUpperCase(),
      })
      const userQualif: UserQualification = staffResponse?.data?.content[0] || null

      await createOrUpdateManHour({
        data: {
          ...manHour,
          ...data,
          appl: data?.appl || userQualif?.applicability || '',
          mordate: data.date && format(data.date, 'P'),
          date: data.date && format(data.date, 'P'),
          mohdep: data.mohdep && format(parse(data.mohdep, 'HH:mm', new Date()), 'HHmm'),
          datefin: data.datefin && format(data.datefin, 'P'),
          hfin: data.hfin && format(parse(data.hfin, 'HH:mm', new Date()), 'HHmm'),
        },
        params: { lang: lang },
        method: displayMode === DisplayModus.CREATE ? 'POST' : 'PUT',
      })
      reset()
      onPostSubmit()
    } catch (err) {
      const messageBackend = err.response.data.messages ? err.response.data.messages[0] : err.response.data
      if (Object.prototype.hasOwnProperty.call(messagesErrWarnFromBack, messageBackend?.amasisCode)) {
        const messageFromServer = messagesErrWarnFromBack[messageBackend?.amasisCode]
        messageFromServer.fieldNames.forEach((fieldName: string) => setError(fieldName, t(messageBackend?.amasisCode)))
      }
    } finally {
      setIsLoading(false)
    }
  }

  const onError: SubmitErrorHandler<ManHourInput> = (errors) => {
    manageFormErrors(errors, (s: string, t: string) => handleDisplayMessage(new Message(s, formTitle, null, t)))
  }

  const handleConfirm = () => {
    setConfirmOpen(false)
    reset()
    onClose()
  }

  const handleStaffSelected = (staff: UserQualification) => {
    setModalStaffOpened(false)
    setValue('user', staff?.code)
    setValue('qualification', staff?.qualification)
    setValue('appl', staff?.applicability)
  }

  const handleQualificationSelected = (qualification: UserQualification) => {
    setModalQualificationOpened(false)
    setValue('qualification', qualification?.qualification)
    setValue('appl', qualification?.applicability)
  }

  const handleClose = () => {
    if (isDirty) setConfirmOpen(true)
    else onClose()
  }

  const { getBackgroundColor } = useContext(LightDarkContext)

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }}>
          <PageHeader
            lang={lang}
            pageTitle={formTitle}
            modalEmbedded={true}
            modalOnClose={onClose}
            style={{ top: 0 }}
            localLoading={isLoading}
          />
          <Container style={{ flex: 1 }} fixed>
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit, onError)}>
                <div style={{ margin: '20px 50px' }}>
                  <Row justify='space-between'>
                    <Col md={5} xs={12}>
                      <FormDate
                        label={t('7000341')}
                        name='date'
                        readOnly={displayMode === DisplayModus.WRITE}
                        rules={{
                          required: displayMode === DisplayModus.CREATE,
                          validate: isValid,
                        }}
                        error={Boolean(errors?.date)}
                      />
                    </Col>
                    <Col md={5} xs={12}>
                      <FormTime label={t('9510096')} name='mohdep' error={Boolean(errors.mohdep)} />
                    </Col>
                  </Row>
                  <Row justify='space-between'>
                    <Col md={5} xs={12}>
                      <FormDate
                        label={t('1LMD002')}
                        name='datefin'
                        readOnly={displayMode === DisplayModus.WRITE}
                        rules={{
                          required: displayMode === DisplayModus.CREATE,
                        }}
                        error={Boolean(errors?.datefin)}
                      />
                    </Col>
                    <Col md={5} xs={12}>
                      <FormTime label={t('9510097')} name='hfin' error={Boolean(errors.hfin)} />
                    </Col>
                  </Row>
                  <Row justify='space-between'>
                    <Col md={5} xs={12} />
                    <Col md={5} xs={12}>
                      <FormTime label={t('7002623')} name='time' error={Boolean(errors.hfin)} />
                    </Col>
                  </Row>
                  <Row justify='space-between'>
                    <Col md={5} xs={12}>
                      <FormInput
                        name='user'
                        label={
                          <>
                            {t('9100287')}
                            <FontAwesomeIcon
                              icon={faExternalLinkAlt}
                              className={displayMode !== DisplayModus.WRITE ? 'color_primary' : 'color_disabled'}
                              style={{ marginLeft: '1ex' }}
                              onClick={() => setModalStaffOpened(true)}
                            />
                          </>
                        }
                        maxLength={40}
                        capitalize
                        error={Boolean(errors.user)}
                        readOnly={displayMode === DisplayModus.WRITE}
                        rules={{ required: displayMode === DisplayModus.CREATE }}
                        fullWidth
                      />
                    </Col>
                    <Col md={5} xs={12}>
                      <FormInput
                        name='qualification'
                        label={
                          <>
                            {t('7000914')}
                            <FontAwesomeIcon
                              icon={faExternalLinkAlt}
                              className={
                                selectedUser && displayMode !== DisplayModus.WRITE ? 'color_primary' : 'color_disabled'
                              }
                              style={{ marginLeft: '1ex' }}
                              onClick={() => (selectedUser ? setModalQualificationOpened(true) : null)}
                            />
                          </>
                        }
                        readOnly={!selectedUser || displayMode === DisplayModus.WRITE}
                        maxLength={15}
                        capitalize
                        error={Boolean(errors.qualification)}
                        fullWidth
                      />
                    </Col>
                  </Row>
                </div>
                <Inline
                  align='space-around'
                  style={{
                    padding: '10px',
                    position: 'absolute',
                    bottom: 0,
                    marginTop: '1em',
                    width: '100%',
                  }}
                >
                  <Button onClick={handleClose} variant='secondary'>
                    {t('8000569')}
                  </Button>
                  <Button type='submit' variant='primary' disabled={isLoading || !isDirty}>
                    {t('9500174')}
                  </Button>
                </Inline>
              </form>
            </FormProvider>
          </Container>
        </div>
      </Modal>
      <ModalConfirm
        confirmMessage={t('GEN0090')}
        isOpened={confirmOpen}
        onCloseModal={() => setConfirmOpen(false)}
        onSubmit={handleConfirm}
      />
      <ModalStaff
        lang={lang}
        isOpened={modalStaffOpened}
        onCloseModal={() => setModalStaffOpened(false)}
        onStaffSelected={handleStaffSelected}
      />
      <ModalQualification
        lang={lang}
        isOpened={modalQualificationOpened}
        surcode={selectedUser}
        onCloseModal={() => setModalQualificationOpened(false)}
        onQualificationSelected={handleQualificationSelected}
      />
    </>
  )
}

export default ModalManHour
